/**
 * Tailwind class maps for the Review application modal.
 *
 * These are utilities rather than component CSS, and that is deliberate: Angular
 * cannot bind a utility containing a `/` through `[class.x]` — it reads the slash as
 * syntax — so every state-dependent class has to arrive as one string. Same approach
 * as `cancel-subscription.styles.ts`.
 */

/* ------------------------------------------------------------------ network rows */

const ROW_BASE =
  'flex items-center gap-3 rounded-md border border-border bg-card px-3.5 py-2.5 transition-colors duration-150';

/** An excluded row must read as *off*, not as disabled — every figure stays legible. */
const ROW_EXCLUDED = 'opacity-45';

const ROW_INTERACTIVE = 'cursor-pointer hover:bg-muted focus-within:ring-2 focus-within:ring-primary';

export function rowClass(options: { included: boolean; interactive: boolean }): string {
  return [
    ROW_BASE,
    options.interactive ? ROW_INTERACTIVE : 'cursor-not-allowed',
    options.included ? '' : ROW_EXCLUDED,
  ]
    .filter(Boolean)
    .join(' ');
}

/* -------------------------------------------------------------------- checkboxes */

const BOX_BASE =
  'grid size-[19px] shrink-0 place-items-center rounded-sm border-[1.5px] transition-colors duration-150';
const BOX_ON = 'border-primary bg-primary text-white';
const BOX_OFF = 'border-zinc-400 bg-card text-transparent dark:border-zinc-500';
/** The primary network's box: checked, but not a control. */
const BOX_LOCKED = 'opacity-30 cursor-not-allowed';

export function checkboxClass(options: { included: boolean; disabled?: boolean }): string {
  return [
    BOX_BASE,
    options.included ? BOX_ON : BOX_OFF,
    options.disabled ? BOX_LOCKED : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/* --------------------------------------------------------------- cost breakdown */

/**
 * Excluded lines are struck through rather than removed. The brand needs to see the
 * money they are choosing *not* to spend; a disappearing row hides the decision.
 */
const LINE_STRUCK = 'line-through decoration-zinc-400 dark:decoration-zinc-500';

export function breakdownRowClass(included: boolean): string {
  return [
    'flex items-center justify-between gap-4 border-b border-border px-4 py-2.5',
    included ? '' : LINE_STRUCK,
  ]
    .filter(Boolean)
    .join(' ');
}

export function breakdownLabelClass(included: boolean): string {
  return included ? 'text-sm text-secondary' : 'text-sm text-muted';
}

export function breakdownFigureClass(included: boolean): string {
  return included
    ? 'font-heading text-[15px] font-bold text-primary'
    : 'font-heading text-[15px] font-bold text-muted';
}

/* ----------------------------------------------------------------------- footer */

/** The wallet figure turns hostile when the hire would overdraw the account. */
export function walletAfterClass(negative: boolean): string {
  return negative
    ? 'font-heading text-2xl font-extrabold tracking-tight text-error'
    : 'font-heading text-2xl font-extrabold tracking-tight text-primary';
}
